import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { FgLogo } from "@/components/FgLogo";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SinistriTab, PagamentiTab, UtentiTab, ImpostazioniTab, MulteTab, DashboardTab, ConsegneTab } from "@/components/AdminTabsExtra";
import { Check, Eye, EyeOff, Loader2, Lock, LogOut, Mail, Plus, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const _reauthCache = { tok: null, exp: 0 }; // A3: token di riautenticazione recente (5 minuti)
const CATEGORIES = ["CITY", "COMPACT", "SUV / CROSSOVER", "SPORT", "PREMIUM"];
const TABS = ["Dashboard", "Prezzi", "Flotta", "Prenotazioni", "Clienti", "Sinistri", "Consegne", "Multe", "Pagamenti", "Richieste LT", "Partner", "Utenti", "WhatsApp", "Gestionale", "Impostazioni", "Password"];

const api = (token) =>
  axios.create({
    baseURL: `${API}/admin`,
    headers: { Authorization: `Bearer ${token}` },
  });

const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(data.access_token, data.refresh_token, data.user?.must_change_password);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-card border border-gray-100 p-6" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-5">
          <FgLogo size={40} />
          <div className="leading-tight">
            <p className="font-extrabold text-fr-navy">Gestionale</p>
            <p className="text-[11px] text-gray-400">FabriRent Napoli • area riservata</p>
          </div>
        </div>
        <label className="block mb-3">
          <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase flex items-center gap-1"><Mail size={11} /> Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
            placeholder="admin@fabrirent.it"
            data-testid="admin-login-email"
          />
        </label>
        <label className="block mb-4">
          <span className="text-[10px] font-bold tracking-wider text-gray-400 uppercase flex items-center gap-1"><Lock size={11} /> Password</span>
          <span className="mt-1 flex items-center relative">
            <input
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 pr-10 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
              placeholder="••••••••"
              data-testid="admin-login-password"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 text-gray-400" aria-label="Mostra password" data-testid="admin-login-toggle-password">
              {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </span>
        </label>
        {error && <p className="mb-3 text-xs text-fr-red font-semibold" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-fr-navy text-white font-bold text-sm py-3 hover:bg-fr-navydark transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />} Entra
        </button>
        <a href="/" className="block mt-4 text-center text-[11px] text-gray-400 hover:text-fr-navy underline underline-offset-2" data-testid="admin-back-to-site">
          ← Torna al sito
        </a>
      </form>
    </div>
  );
};

/* D1 (ITERAZIONE 4): regole di prezzo (stagioni, durata, weekend, date speciali) per veicolo o categoria + anteprima */
const PricingRulesEditor = ({ token, vehicles }) => {
  const [scope, setScope] = useState("veicolo");
  const [target, setTarget] = useState("");
  const [rule, setRule] = useState({ weekend_pct: "", seasons: [], duration_discounts: [], special_dates: [] });
  const [prev, setPrev] = useState(null);
  const [vehicleId, setVehicleId] = useState("");
  const cats = ["CITY", "COMPACT", "SUV / CROSSOVER", "SPORT", "PREMIUM"];

  useEffect(() => {
    if (!target) return;
    if (scope === "veicolo") {
      const v = vehicles.find((x) => x.targa === target);
      setVehicleId(v?.id || "");
      setRule({ weekend_pct: v?.pricing?.weekend_pct ?? "", seasons: v?.pricing?.seasons || [], duration_discounts: v?.pricing?.duration_discounts || [], special_dates: v?.pricing?.special_dates || [] });
    } else {
      api(token).get("/pricing-rules").then(({ data }) => { const r = data.rules[target] || {}; setRule({ weekend_pct: r.weekend_pct ?? "", seasons: r.seasons || [], duration_discounts: r.duration_discounts || [], special_dates: r.special_dates || [] }); }).catch(() => {});
    }
  }, [target, scope, vehicles]); // eslint-disable-line

  const save = async () => {
    try {
      const body = { scope, target: scope === "veicolo" ? target.toUpperCase() : target };
      ["weekend_pct", "seasons", "duration_discounts", "special_dates"].forEach((k) => { if (rule[k] !== "" && rule[k] !== null) body[k] = rule[k]; });
      await api(token).post("/pricing-rules", body);
      toast.success("Regole salvate");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const preview = async () => {
    if (!vehicleId) return;
    try {
      const from = new Date(); from.setDate(from.getDate() + 1);
      const to = new Date(); to.setDate(to.getDate() + 4);
      const { data } = await api(token).get(`/pricing-preview?vehicle_id=${vehicleId}&pickup=${from.toISOString()}&dropoff=${to.toISOString()}`);
      setPrev(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4" data-testid="pricing-rules-editor">
      <p className="text-sm font-extrabold text-fr-navy">Regole di prezzo (stagioni, durata, weekend, date speciali)</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select value={scope} onChange={(e) => { setScope(e.target.value); setTarget(""); setPrev(null); }} className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-scope">
          <option value="veicolo">Singolo veicolo</option>
          <option value="categoria">Tutta la categoria</option>
        </select>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-target">
          <option value="">— scegli {scope === "veicolo" ? "targa" : "categoria"} —</option>
          {(scope === "veicolo" ? vehicles.map((v) => v.targa) : cats).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="text-[10px] font-bold text-gray-400 uppercase">Weekend +%
          <input type="number" value={rule.weekend_pct} onChange={(e) => setRule({ ...rule, weekend_pct: e.target.value })} className="block h-8 w-20 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-weekend" />
        </label>
        <label className="text-[10px] font-bold text-gray-400 uppercase">Sconto da N giorni
          <input placeholder='es [{"min_days":7,"pct":10}]' onChange={(e) => { try { setRule({ ...rule, duration_discounts: JSON.parse(e.target.value || "[]") }); } catch {} }} className="block h-8 w-44 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-duration" />
        </label>
        <label className="text-[10px] font-bold text-gray-400 uppercase">Stagioni (da-a,+%)
          <input placeholder='es [{"from":"2026-07-01","to":"2026-08-31","pct":20}]' onChange={(e) => { try { setRule({ ...rule, seasons: JSON.parse(e.target.value || "[]") }); } catch {} }} className="block h-8 w-52 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-seasons" />
        </label>
        <label className="text-[10px] font-bold text-gray-400 uppercase">Date speciali (+€)
          <input placeholder='es [{"date":"2026-12-31","price":10}]' onChange={(e) => { try { setRule({ ...rule, special_dates: JSON.parse(e.target.value || "[]") }); } catch {} }} className="block h-8 w-48 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="rules-special" />
        </label>
        <button onClick={save} disabled={!target} className="h-8 rounded-lg bg-fr-navy text-white text-[11px] font-bold px-3 disabled:opacity-50" data-testid="rules-save">Salva regole</button>
        <button onClick={preview} disabled={!vehicleId} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 disabled:opacity-50" data-testid="rules-preview">Anteprima (3 giorni da domani)</button>
      </div>
      {prev && (
        <p className="mt-2 text-[11px] text-gray-600" data-testid="rules-preview-result">
          Anteprima {prev.targa} • {prev.days} giorni: base €{prev.base} → con regole <b>€{prev.final}</b> (Δ €{prev.delta})
        </p>
      )}
    </div>
  );
};

/* COMANDO SITO FASE 1.1/4 — pagina "Collegamento gestionale" (SOLO TITOLARE):
   indirizzo + chiave API (salvata CIFRATA nel DB, mai mostrata per intero, mai al browser) +
   "Prova collegamento" con esito chiaro + interruttori di ciò che passa al gestionale. */
const GestionaleTab = ({ token, me }) => {
  const [g, setG] = useState({ api_url: "https://mobility-control-2.preview.emergentagent.com", api_key: "", enabled: false, api_key_masked: "", switches: {} });
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api(token).get("/admin/gestionale-settings").then(({ data }) => setG((x) => ({ ...x, ...data, api_key: "" }))).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const prova = async () => {
    setBusy(true);
    try {
      const { data } = await api(token).post("/admin/gestionale-test", { api_url: g.api_url, api_key: g.api_key });
      setTest(data.ok ? { ok: true, msg: `Collegamento riuscito — ${data.models} modelli visibili` } : { ok: false, msg: data.error || "Collegamento non riuscito" });
    } catch (e) { setTest({ ok: false, msg: formatDetail(e.response?.data?.detail) || "Errore di rete" }); } finally { setBusy(false); }
  };
  const salva = async (enabled) => {
    try {
      await api(token).post("/admin/gestionale-settings", { api_url: g.api_url, api_key: g.api_key, enabled: enabled ?? g.enabled });
      toast.success(enabled ? "Collegamento attivato: flotta, prezzi e prenotazioni passano al gestionale" : "Collegamento salvato");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const sw = async (k, v) => {
    try { await api(token).post("/admin/switches", { [k]: v }); toast.success("Interruttore aggiornato"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-tab-gestionale">
      <p className="text-xs text-gray-500">Il gestionale FABRI GROUP è l'unica fonte di flotta, prezzi, disponibilità, clienti, contratti, cauzioni e incassi. La chiave API è salvata <b>cifrata</b> nel database del sito: non viene mai mostrata per intero, né inviata al browser, né scritta nei log.</p>
      <label className="block text-[10px] font-bold text-gray-400 uppercase">Indirizzo del gestionale
        <input value={g.api_url} onChange={(e) => setG({ ...g, api_url: e.target.value })} placeholder="https://mobility-control-2.preview.emergentagent.com" className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="gestionale-url" />
      </label>
      <label className="block text-[10px] font-bold text-gray-400 uppercase">Chiave API {g.api_key_masked && <span className="font-mono normal-case text-gray-500">(attuale: {g.api_key_masked})</span>}
        <input value={g.api_key} onChange={(e) => setG({ ...g, api_key: e.target.value })} placeholder="incolla la chiave (X-Api-Key)" className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm font-mono" data-testid="gestionale-key" />
      </label>
      <div className="flex flex-wrap gap-2">
        <button onClick={prova} disabled={busy} className="h-9 rounded-lg border border-fr-navy text-fr-navy text-xs font-bold px-4 disabled:opacity-50" data-testid="gestionale-test">Prova collegamento</button>
        <button onClick={() => salva(false)} className="h-9 rounded-lg bg-gray-100 text-gray-700 text-xs font-bold px-4" data-testid="gestionale-save">Salva</button>
        <button onClick={() => salva(true)} disabled={busy} className="h-9 rounded-lg bg-green-600 text-white text-xs font-bold px-4" data-testid="gestionale-enable">Attiva collegamento</button>
        {g.enabled && <span className="text-xs font-bold text-green-700 self-center" data-testid="gestionale-enabled-badge">● ATTIVO ({g.api_key_masked})</span>}
      </div>
      {test && <p className={`text-xs font-bold p-2 rounded-lg ${test.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-fr-red"}`} data-testid="gestionale-test-result">{test.ok ? "✓ " : "✗ "}{test.msg}</p>}
      <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="gestionale-switches">
        <p className="text-sm font-extrabold text-fr-navy">Interruttori (niente viene cancellato: si accende e si spegne)</p>
        {[
          ["site_payments", "Pagamenti e preautorizzazioni Stripe del sito, cauzioni, rimborsi"],
          ["site_contract_sign", "Firma del contratto nel sito"],
          ["site_local_pricing", "Motore prezzi e flotta locali (uso pubblico)"],
          ["partner_flow", "Flusso hotel/partner (invariato; spento finché il titolare non decide)"],
        ].map(([k, label]) => (
          <label key={k} className="mt-2 flex items-center gap-2 text-xs text-gray-700 cursor-pointer" data-testid={`switch-${k}`}>
            <input type="checkbox" checked={!!g.switches?.[k]} onChange={(e) => sw(k, e.target.checked)} className="accent-fr-navy" />
            <span>{label}</span>
          </label>
        ))}
        <p className="text-[10px] text-gray-400 mt-2">Attivando il collegamento, questi servizi passano al gestionale (spenti di default qui).</p>
      </div>
    </div>
  );
};

const PrezziTab = ({ token }) => {
  const [vehicles, setVehicles] = useState([]);
  const [savingId, setSavingId] = useState("");
  const load = () => { api(token).get("/vehicles").then(({ data }) => setVehicles(data.vehicles)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);


  const saveVehicle = async (v) => {
    setSavingId(v.id);
    try {
      await api(token).post("/vehicles", {
        id: v.id, category: v.category, tipo: v.tipo || "auto", targa: v.targa, brand: v.brand, model: v.model,
        years: v.years, cambio: v.cambio, alimentazione: v.alimentazione,
        daily_price: v.daily_price === "" || v.daily_price == null ? null : Number(v.daily_price),
        active: v.active !== false,
      });
      toast.success(`${v.targa}: salvato`);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setSavingId(""); }
  };

  const setV = (id, key, val) => setVehicles((vs) => vs.map((x) => (x.id === id ? { ...x, [key]: val } : x)));

  return (
    <div data-testid="admin-panel-prezzi">
      <PricingRulesEditor token={token} vehicles={vehicles} />
      <p className="text-xs text-gray-500 mb-4">
        Prezzo giornaliero <b>auto per auto</b> (per singolo veicolo/targa) — niente più tariffa unica di categoria. Campo vuoto = "DA GESTIONALE" sul sito. Qui puoi anche correggere cambio e alimentazione di ogni auto.
      </p>
      <div className="space-y-1.5" data-testid="admin-prices-vehicles">
        {vehicles.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2" data-testid={`admin-price-row-${v.targa}`}>
            <span className="text-[9px] font-extrabold bg-fr-ink text-white rounded px-1.5 py-0.5 w-fit">{v.category}</span>
            <span className="text-xs font-bold text-fr-navy w-20 font-mono">{v.targa}</span>
            <span className="text-[11px] text-gray-500 w-40 truncate">{v.brand} {v.model} ({v.years})</span>
            <input
              type="number" min="1" step="0.5"
              value={v.daily_price ?? ""}
              onChange={(e) => setV(v.id, "daily_price", e.target.value)}
              placeholder="— da gestionale —"
              className="h-8 w-28 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm outline-none focus:border-fr-navy focus:bg-white"
              data-testid={`admin-price-input-${v.targa}`}
            />
            <select value={v.cambio || "manuale"} onChange={(e) => setV(v.id, "cambio", e.target.value)} className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid={`admin-cambio-${v.targa}`}>
              <option value="manuale">Manuale</option>
              <option value="automatico">Automatico</option>
            </select>
            <select value={v.alimentazione || "benzina"} onChange={(e) => setV(v.id, "alimentazione", e.target.value)} className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid={`admin-alimentazione-${v.targa}`}>
              {["benzina", "diesel", "ibrida", "elettrica", "gpl"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <button onClick={() => saveVehicle(v)} disabled={savingId === v.id} className="h-8 rounded-lg bg-fr-navy text-white text-[11px] font-bold px-2.5 flex items-center gap-1 disabled:opacity-60" data-testid={`admin-price-save-${v.targa}`}>
              {savingId === v.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salva
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const FlottaTab = ({ token }) => {
  const [vehicles, setVehicles] = useState([]);
  const [cal, setCal] = useState(null);
  const [nw, setNw] = useState({ category: "CITY", tipo: "auto", targa: "", brand: "", model: "", years: "", cambio: "manuale", alimentazione: "benzina", daily_price: "" });
  const [blocks, setBlocks] = useState({ targa: "", kind: "manutenzione", reason: "", from_dt: "2026-10-25T09:00", to_dt: "2026-10-26T09:00" });
  const load = () => { api(token).get("/vehicles").then(({ data }) => setVehicles(data.vehicles)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const add = async () => {
    try {
      await api(token).post("/vehicles", { ...nw, id: null, daily_price: nw.daily_price === "" ? null : Number(nw.daily_price) });
      toast.success("Veicolo aggiunto alla flotta");
      setNw({ ...nw, targa: "", brand: "", model: "", years: "", daily_price: "" });
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    try { await api(token).delete(`/vehicles/${id}`); toast.success("Veicolo rimosso"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (v) => {
    try {
      await api(token).post("/vehicles", { id: v.id, category: v.category, tipo: v.tipo || "auto", targa: v.targa, brand: v.brand, model: v.model, years: v.years, cambio: v.cambio, alimentazione: v.alimentazione, daily_price: v.daily_price, active: !v.active });
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const openCal = async (v) => {
    try {
      const { data } = await api(token).get(`/vehicles/${v.id}/calendar`);
      setCal(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const addBlock = async () => {
    try {
      const v = vehicles.find((x) => x.targa === blocks.targa);
      if (!v) { toast.error("Scrivi una targa presente in flotta"); return; }
      await api(token).patch(`/vehicles/${v.id}/blocks`, blocks);
      toast.success("Blocco/manutenzione registrata: la targa è indisponibile nel periodo");
      openCal(v);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-tab-flotta">
      <div className="flex flex-wrap items-end gap-2 bg-white border border-gray-200 rounded-xl p-3 mb-4" data-testid="admin-vehicle-new">
        <select value={nw.category} onChange={(e) => setNw({ ...nw, category: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs">
          {["CITY", "COMPACT", "SUV / CROSSOVER", "SPORT", "PREMIUM", "SCOOTER"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={nw.tipo} onChange={(e) => setNw({ ...nw, tipo: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs">
          <option value="auto">Auto</option>
          <option value="scooter">Scooter</option>
        </select>
        <input value={nw.targa} onChange={(e) => setNw({ ...nw, targa: e.target.value.toUpperCase() })} placeholder="Targa" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs uppercase" data-testid="admin-vehicle-new-targa" />
        <input value={nw.brand} onChange={(e) => setNw({ ...nw, brand: e.target.value })} placeholder="Marca" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <input value={nw.model} onChange={(e) => setNw({ ...nw, model: e.target.value })} placeholder="Modello" className="h-9 w-32 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <input value={nw.years} onChange={(e) => setNw({ ...nw, years: e.target.value })} placeholder="2021-2023" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <select value={nw.cambio} onChange={(e) => setNw({ ...nw, cambio: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs">
          <option value="manuale">Manuale</option>
          <option value="automatico">Automatico</option>
        </select>
        <select value={nw.alimentazione} onChange={(e) => setNw({ ...nw, alimentazione: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs">
          {["benzina", "diesel", "ibrida", "elettrica", "gpl"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input type="number" value={nw.daily_price} onChange={(e) => setNw({ ...nw, daily_price: e.target.value })} placeholder="€/g" className="h-9 w-20 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <button onClick={add} className="h-9 rounded-lg bg-fr-navy text-white text-xs font-bold px-3 flex items-center gap-1 hover:bg-fr-navydark" data-testid="admin-vehicle-add">
          <Plus size={13} /> Aggiungi
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4" data-testid="admin-vehicle-blocks">
        <p className="text-[11px] font-bold text-amber-800 w-full">Manutenzione / blocco indisponibilità (il calendario e la disponibilità la rispettano):</p>
        <input value={blocks.targa} onChange={(e) => setBlocks({ ...blocks, targa: e.target.value.toUpperCase() })} placeholder="Targa" className="h-9 w-24 rounded-lg border border-gray-200 bg-white px-2 text-xs uppercase" />
        <select value={blocks.kind} onChange={(e) => setBlocks({ ...blocks, kind: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs">
          <option value="manutenzione">Manutenzione</option>
          <option value="pulizia">Pulizia</option>
          <option value="blocco">Blocco</option>
        </select>
        <input value={blocks.reason} onChange={(e) => setBlocks({ ...blocks, reason: e.target.value })} placeholder="Motivo (es. revisione, gomme)" className="h-9 w-44 rounded-lg border border-gray-200 bg-white px-2 text-xs" />
        <input type="datetime-local" value={blocks.from_dt} onChange={(e) => setBlocks({ ...blocks, from_dt: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs" />
        <input type="datetime-local" value={blocks.to_dt} onChange={(e) => setBlocks({ ...blocks, to_dt: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs" />
        <button onClick={addBlock} className="h-9 rounded-lg bg-fr-ink text-white text-xs font-bold px-3">Aggiungi blocco</button>
      </div>

      <div className="space-y-1.5" data-testid="admin-vehicles-list">
        {vehicles.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2" data-testid={`admin-vehicle-${v.targa}`}>
            <span className="text-[9px] font-extrabold bg-fr-ink text-white rounded px-1.5 py-0.5 w-fit">{v.category}</span>
            <span className={`text-[9px] font-bold rounded px-1 py-0.5 ${v.tipo === "scooter" ? "bg-fr-sun text-fr-ink" : "bg-gray-100 text-gray-500"}`}>{v.tipo}</span>
            <span className="text-xs font-bold text-fr-navy w-20 font-mono">{v.targa}</span>
            <span className="text-[11px] text-gray-500">{v.brand} {v.model} ({v.years}) • {v.cambio} • {v.alimentazione} • {v.daily_price != null ? `€ ${v.daily_price}/g` : "da gestionale"}</span>
            {v.active === false && <span className="text-[10px] font-bold text-fr-red">DISATTIVO</span>}
            <div className="ml-auto flex gap-1.5">
              <button onClick={() => openCal(v)} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[10px] font-bold px-2" data-testid={`admin-vehicle-cal-${v.targa}`}>Calendario</button>
              <button onClick={() => toggleActive(v)} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[10px] font-bold px-2">{v.active === false ? "Attiva" : "Disattiva"}</button>
              <button onClick={() => remove(v.id)} className="h-8 rounded-lg bg-red-50 text-fr-red text-[10px] font-bold px-2 hover:bg-red-100" data-testid={`admin-vehicle-delete-${v.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {cal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCal(null)} data-testid="admin-vehicle-calendar">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-extrabold text-fr-navy">Calendario {cal.targa} <span className="text-xs font-normal text-gray-400">{cal.vehicle?.brand} {cal.vehicle?.model}</span></p>
              <button onClick={() => setCal(null)} className="text-xs font-bold text-gray-400">Chiudi</button>
            </div>
            {cal.bookings.length === 0 && cal.blocks.length === 0 && <p className="text-xs text-gray-400 mt-3">Nessuna prenotazione né blocco per questa targa.</p>}
            {cal.bookings.map((b) => (
              <div key={b.reference} className="mt-2 rounded-lg border border-gray-100 bg-gray-50 p-2 text-[11px]" data-testid={`cal-booking-${b.reference}`}>
                <b className="font-mono text-fr-navy">{b.reference}</b> — {b.status} — {b.customer?.first_name} {b.customer?.last_name}
                <br />{format(new Date(b.pickup_at), "d MMM HH:mm", { locale: it })} → {format(new Date(b.dropoff_at), "d MMM HH:mm", { locale: it })}
              </div>
            ))}
            {cal.blocks.map((bl, i) => (
              <div key={i} className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid={`cal-block-${i}`}>
                {bl.kind}: {bl.reason || "—"} <br />{format(new Date(bl.from), "d MMM HH:mm", { locale: it })} → {format(new Date(bl.to), "d MMM HH:mm", { locale: it })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PrenotazioniTab = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [holds, setHolds] = useState([]);
  const [docView, setDocView] = useState(null);
  const [propose, setPropose] = useState(null);
  const load = () => { api(token).get("/bookings").then(({ data }) => { setBookings(data.bookings); setHolds(data.active_holds || []); }).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const submitProposal = async () => {
    try {
      const body = { note: propose.note };
      if (propose.pickup) body.pickup_at = new Date(propose.pickup).toISOString();
      if (propose.dropoff) body.dropoff_at = new Date(propose.dropoff).toISOString();
      if (propose.targa) body.targa = propose.targa;
      const { data } = await api(token).post(`/bookings/${propose.ref}/propose-change`, body);
      toast.success(`Proposta inviata: €${data.new_price}, scade ${new Date(data.expires_at).toLocaleString("it-IT")}`);
      setPropose(null);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const setStatus = async (ref, status) => {
    try {
      await api(token).patch(`/bookings/${ref}`, { status });
      toast.success(`${ref}: ${status}`);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
      load();
    }
  };

  const setTarga = async (ref, targa) => {
    try {
      await api(token).patch(`/bookings/${ref}`, { targa_assigned: targa.toUpperCase() });
      toast.success(`${ref}: targa ${targa.toUpperCase()} assegnata (sovrapposizioni verificate)`);
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const setCargos = async (ref, ricevuta) => {
    try {
      await api(token).patch(`/bookings/${ref}/cargos`, { data_trasmissione: new Date().toISOString().slice(0, 10), numero_ricevuta: ricevuta });
      toast.success(`${ref}: CaRGOS registrato`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const createContract = async (ref) => {
    try {
      const { data } = await api(token).post(`/bookings/${ref}/contract`);
      toast.success(`Contratto PDF generato (${data.hash.slice(0, 12)}…) — modello [DA VALIDARE CON LEGALE]`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const sendOtp = async (ref) => {
    try {
      const { data } = await api(token).post(`/contracts/${ref}/send-otp`);
      toast.success(data.sent === "inviato" ? `OTP inviato a ${data.sent_to}` : `OTP non inviato: ${data.error || "attiva la firma dal pannello (ambiente)"}`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const viewDocP = async (path) => {
    try {
      const r = await axios.get(`${API}/account/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setDocView(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-tab-prenotazioni">
      {(holds || []).filter((h2) => h2.expiring_soon).length > 0 && (
        <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800" data-testid="admin-holds-warning">
          <p className="font-bold">Blocchi in scadenza (avviso 10 min prima):</p>
          {holds.filter((h2) => h2.expiring_soon).map((h2) => (
            <p key={h2.reference}>{h2.reference} — {h2.category} — scade tra {h2.minutes_left} min</p>
          ))}
        </div>
      )}
      <button onClick={load} className="mb-3 h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200">
        <RefreshCw size={12} /> Aggiorna
      </button>
      {bookings.length === 0 && <p className="text-sm text-gray-400">Nessuna prenotazione.</p>}
      <div className="space-y-2" data-testid="admin-bookings-list">
        {bookings.map((b) => (
          <div key={b.reference} className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`admin-booking-${b.reference}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-mono font-extrabold text-fr-navy text-sm">{b.reference}</span>
              <span className="text-[10px] font-bold bg-fr-ink text-white rounded px-1.5 py-0.5">{b.category}</span>
              {b.preferred_model && <span className="text-[10px] text-gray-500 font-semibold">→ {b.preferred_model}</span>}
              {b.channel && b.channel !== "sito" && <span className="text-[10px] font-bold text-gray-400">canale: {b.channel}</span>}
              {(b.flags || []).map((f) => (
                <span key={f} className="text-[10px] font-bold bg-red-50 text-fr-red rounded px-1.5 py-0.5" title="Segnalato all'admin, non rifiutato in automatico">⚠ {f}</span>
              ))}
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${["confermata", "in_corso", "rientrata", "conclusa"].includes(b.status) ? "bg-green-100 text-green-800" : ["annullata", "no_show"].includes(b.status) ? "bg-red-100 text-fr-red" : "bg-amber-100 text-amber-800"}`}>
                {b.status.replace("_", " ").toUpperCase()}
              </span>
              <select
                value={b.status}
                onChange={(e) => setStatus(b.reference, e.target.value)}
                className="ml-auto h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs"
                data-testid={`admin-booking-status-${b.reference}`}
              >
                {["richiesta", "in_verifica", "confermata", "annullata", "no_show", "in_corso", "rientrata", "conclusa"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <p className="text-xs text-gray-600 mt-1.5">
              {b.customer.first_name} {b.customer.last_name} • {b.customer.phone} • {b.customer.email}
              {b.email_verified ? (
                <span className="ml-1 text-[10px] font-bold text-green-700" data-testid={`booking-email-verified-${b.reference}`}>✓ email verificata</span>
              ) : (
                <span className="ml-1 text-[10px] font-bold text-fr-red" data-testid={`booking-email-unverified-${b.reference}`}>✗ email NON verificata: conferma bloccata</span>
              )}
              {b.account_email && <span className="text-[10px] text-fr-navy font-bold"> • account: {b.account_email}</span>}
              {b.partner_email && <span className="text-[10px] text-fr-red font-bold"> • partner: {b.partner_email}</span>}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {b.location_name} • {format(new Date(b.pickup_at), "d MMM HH:mm", { locale: it })} → {format(new Date(b.dropoff_at), "d MMM HH:mm", { locale: it })} • {b.days} giorni • WhatsApp: {b.whatsapp_status}
              {b.total_price != null && <> • Totale cliente: <b className="text-fr-navy">€ {b.total_price}</b></>}
              {b.commission_amount != null && <> • Commissione partner: <b className="text-green-700">€ {b.commission_amount}</b></>}
              {(b.driver_age || b.license_years) && <> • età: {b.driver_age || "?"} • patente: {b.license_years || "?"} anni</>}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <input
                defaultValue={b.targa_assigned || ""}
                onBlur={(e) => e.target.value !== b.targa_assigned && setTarga(b.reference, e.target.value)}
                placeholder="Assegna targa"
                className="h-8 w-32 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs uppercase"
                data-testid={`admin-booking-targa-${b.reference}`}
              />
              {b.customer_docs?.map((d, i) => (
                <button key={i} onClick={() => viewDocP(d.path)} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-booking-doc-${i}`}>
                  doc ospite: {d.step}
                </button>
              ))}
              <button onClick={() => createContract(b.reference)} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-booking-contract-${b.reference}`}>Contratto PDF</button>
              {!b.email_verified && b.customer?.email && (
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api(token).post(`/customers/${encodeURIComponent(b.customer.email)}/send-verification`, {});
                      toast[data.otp_ambiente_prova ? "info" : "success"](`Codice inviato a ${b.customer.email}${data.otp_ambiente_prova ? ` (prova: ${data.otp_ambiente_prova})` : ""}`);
                      load();
                    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
                  }}
                  className="text-[10px] font-bold bg-amber-50 text-amber-800 rounded px-2 py-1"
                  data-testid={`admin-booking-sendverify-${b.reference}`}
                >
                  Invia verifica email
                </button>
              )}
              <button onClick={() => setPropose({ ref: b.reference, email: b.customer.email, pickup: b.pickup_at?.slice(0, 16), dropoff: b.dropoff_at?.slice(0, 16), targa: b.targa_assigned || "", note: "" })} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-booking-propose-${b.reference}`}>Proponi modifica</button>
              <button onClick={() => sendOtp(b.reference)} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-booking-otp-${b.reference}`}>Invia OTP firma</button>
              <input
                onBlur={(e) => e.target.value && setCargos(b.reference, e.target.value)}
                placeholder="CaRGOS n. ricevuta"
                className="h-8 w-36 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs"
                data-testid={`admin-booking-cargos-${b.reference}`}
              />
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!docView} onOpenChange={(o) => !o && setDocView(null)}>
        <DialogContent className="max-w-md" data-testid="admin-doc-dialog-booking">
          <DialogTitle>Documento ospite</DialogTitle>
          {docView && <img src={docView} alt="Documento" className="w-full rounded-lg border border-gray-100" />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!propose} onOpenChange={(o) => !o && setPropose(null)}>
        <DialogContent className="max-w-md" data-testid="propose-dialog">
          <DialogTitle>Proposta di modifica — {propose?.ref}</DialogTitle>
          <p className="text-[11px] text-gray-500 mb-2">C5: la prenotazione originale resta valida e invariata finché il cliente non accetta. Scadenza proposta configurabile (Impostazioni).</p>
          {propose && (
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-gray-400 uppercase">Ritiro
                <input type="datetime-local" value={propose.pickup} onChange={(e) => setPropose({ ...propose, pickup: e.target.value })} className="mt-1 w-full h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="propose-pickup" />
              </label>
              <label className="block text-[10px] font-bold text-gray-400 uppercase">Consegna
                <input type="datetime-local" value={propose.dropoff} onChange={(e) => setPropose({ ...propose, dropoff: e.target.value })} className="mt-1 w-full h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="propose-dropoff" />
              </label>
              <label className="block text-[10px] font-bold text-gray-400 uppercase">Targa (facoltativa)
                <input value={propose.targa} onChange={(e) => setPropose({ ...propose, targa: e.target.value.toUpperCase() })} className="mt-1 w-full h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm uppercase" data-testid="propose-targa" />
              </label>
              <label className="block text-[10px] font-bold text-gray-400 uppercase">Note/condizioni per il cliente
                <input value={propose.note} onChange={(e) => setPropose({ ...propose, note: e.target.value })} className="mt-1 w-full h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="propose-note" />
              </label>
              <button onClick={submitProposal} className="w-full rounded-xl bg-fr-navy text-white font-bold text-sm px-4 py-2.5" data-testid="propose-submit">Invia proposta al cliente</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* C2/C6 (ITERAZIONE 4): editor stato documenti — l'operatore verifica e accetta (mai "autentico"). */
const DOC_STATUSES = ["richiesto", "caricato", "in_verifica", "accettato", "rifiutato", "da_integrare"];
const DocStateEditor = ({ token, email, docs }) => {
  const [open, setOpen] = useState(false);
  const save = async (docType, patch) => {
    try {
      await api(token).patch(`/customers/${encodeURIComponent(email)}/documents/${docType}`, patch);
      toast.success(`Documento ${docType} aggiornato`);
      window.dispatchEvent(new CustomEvent("fr-docs-updated"));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid={`doc-editor-${email}`}>
      <button onClick={() => setOpen(!open)} className="text-[10px] font-bold text-fr-navy" data-testid="doc-editor-toggle">
        {open ? "▼" : "▶"} Verifica documenti (operatore)
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {["identita", "patente", "passaporto"].map((t) => {
            const d = docs?.[t] || {};
            return (
              <div key={t} className="flex flex-wrap items-center gap-1.5 bg-gray-50 rounded-lg p-2" data-testid={`doc-row-${t}`}>
                <span className="text-[10px] font-bold text-gray-500 w-16 capitalize">{t}</span>
                <select value={d.status || "richiesto"} onChange={(e) => save(t, { status: e.target.value })} className="h-7 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-status-${t}`}>
                  {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}{s === "accettato" ? " (verifica operatore)" : ""}</option>)}
                </select>
                <input defaultValue={d.expires_at || ""} onBlur={(e) => e.target.value !== (d.expires_at || "") && save(t, { expires_at: e.target.value })} placeholder="scadenza AAAA-MM-GG" className="h-7 w-32 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-expiry-${t}`} />
                <input defaultValue={d.number || ""} onBlur={(e) => e.target.value !== (d.number || "") && save(t, { number: e.target.value })} placeholder="n. documento" className="h-7 w-28 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-number-${t}`} />
                <input defaultValue={d.issued_on || ""} onBlur={(e) => e.target.value !== (d.issued_on || "") && save(t, { issued_on: e.target.value })} placeholder="rilascio AAAA-MM-GG" className="h-7 w-32 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-issued-${t}`} />
                <input defaultValue={d.issuer || ""} onBlur={(e) => e.target.value !== (d.issuer || "") && save(t, { issuer: e.target.value })} placeholder="autorità emittente" className="h-7 w-32 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-issuer-${t}`} />
                {(d.status === "rifiutato" || d.status === "da_integrare") && (
                  <input defaultValue={d.reject_reason || ""} onBlur={(e) => save(t, { reject_reason: e.target.value })} placeholder="motivo" className="h-7 w-32 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`doc-reason-${t}`} />
                )}
                {d.verified_by && <span className="text-[9px] text-green-700">verificato da {d.verified_by}</span>}
              </div>
            );
          })}
          <p className="text-[9px] text-gray-400">Lo stato "accettato" attesta solo la verifica dell'operatore — non l'autenticità del documento.</p>
        </div>
      )}
    </div>
  );
};

const ClientiTab = ({ token }) => {
  const [customers, setCustomers] = useState([]);
  const [docView, setDocView] = useState(null);
  const load = () => { api(token).get("/customers").then(({ data }) => setCustomers(data.customers)).catch(() => {}); };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("fr-docs-updated", h);
    return () => window.removeEventListener("fr-docs-updated", h);
  }, [token]);

  const viewDoc = async (path) => {
    try {
      const r = await axios.get(`${API}/account/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setDocView(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-tab-clienti">
      <button onClick={load} className="mb-3 h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200">
        <RefreshCw size={12} /> Aggiorna
      </button>
      {customers.length === 0 && <p className="text-sm text-gray-400">Nessun cliente registrato.</p>}
      <div className="space-y-2" data-testid="admin-customers-list">
        {customers.map((c) => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-xs" data-testid={`admin-customer-${c.id}`}>
            <p className="font-bold text-fr-navy text-sm">{c.first_name} {c.last_name}
              {c.email_verified ? (
                <span className="ml-2 text-[10px] font-bold text-green-700" data-testid={`customer-email-verified`}>✓ email verificata</span>
              ) : (
                <span className="ml-2 text-[10px] font-bold text-fr-red" data-testid={`customer-email-unverified`}>✗ email non verificata</span>
              )}
            </p>
            <p className="text-gray-600 mt-0.5">{c.email} • {c.phone}</p>
            {(c.address || c.license_number) && (
              <p className="text-[11px] text-gray-500 mt-0.5">
                {c.address ? `🏠 ${c.address}` : ""}{c.license_number ? ` • Patente ${c.license_number}${c.license_expiry ? ` (scad. ${c.license_expiry})` : ""}` : ""}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {(["identity_card", "driver_license"]).map((t) =>
                c.documents?.[t] ? (
                  <button
                    key={t}
                    onClick={() => viewDoc(c.documents[t])}
                    className="rounded-lg border border-fr-navy text-fr-navy text-[10px] font-bold px-2.5 py-1.5 hover:bg-fr-navy hover:text-white transition-colors"
                    data-testid={`admin-doc-view-${t}`}
                  >
                    Vedi {t === "identity_card" ? "carta d'identità" : "patente"}
                  </button>
                ) : (
                  <span key={t} className="rounded-lg bg-gray-50 text-gray-400 text-[10px] font-semibold px-2.5 py-1.5">
                    {t === "identity_card" ? "Carta d'identità" : "Patente"} non caricata
                  </span>
                )
              )}
            </div>
            <DocStateEditor token={token} email={c.email} docs={c.docs || {}} />
            <p className="text-[10px] text-gray-400 mt-1.5">Iscritto il {format(new Date(c.created_at), "d MMMM yyyy", { locale: it })}</p>
          </div>
        ))}
      </div>
      <Dialog open={!!docView} onOpenChange={(o) => !o && setDocView(null)}>
        <DialogContent className="sm:max-w-lg rounded-2xl p-3" data-testid="admin-doc-viewer">
          <DialogTitle className="text-xs font-bold text-gray-500 uppercase tracking-wide">Documento cliente</DialogTitle>
          {docView && <img src={docView} alt="Documento cliente" className="mt-2 max-h-[60vh] w-full object-contain rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RichiesteTab = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const load = () => { api(token).get("/lt-requests").then(({ data }) => setRequests(data.requests)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const patchLT = async (id, body) => {
    try {
      const { data } = await api(token).patch(`/lt-requests/${id}`, body);
      toast.success(data.quote_sent_to ? `Preventivo inviato a ${data.quote_sent_to}` : "Richiesta aggiornata");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-tab-richieste">
      <button onClick={load} className="mb-3 h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200">
        <RefreshCw size={12} /> Aggiorna
      </button>
      {requests.length === 0 && <p className="text-sm text-gray-400">Nessuna richiesta lungo termine.</p>}
      <div className="space-y-2" data-testid="admin-lt-requests-list">
        {requests.map((r) => (
          <div key={r.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-xs" data-testid={`admin-lt-${r.id}`}>
            <div className="flex items-center gap-2">
              <span className="font-bold text-fr-navy text-sm">{r.first_name}</span>
              <span className="text-[10px] font-bold bg-fr-navy text-white rounded px-1.5 py-0.5">{r.months} mesi</span>
              <span className="ml-auto text-[10px] text-gray-400">{format(new Date(r.created_at), "d MMM HH:mm", { locale: it })}</span>
            </div>
            <p className="text-gray-600 mt-0.5">{r.phone}{r.email ? ` • ${r.email}` : ""}{r.notes ? ` • ${r.notes}` : ""}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`admin-lt-actions-${r.id}`}>
              <select value={r.status || "nuova"} onChange={(e) => patchLT(r.id, { status: e.target.value })} className="h-7 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`lt-status-${r.id}`}>
                {["nuova", "in_lavorazione", "preventivo_inviato", "accettata", "rifiutata"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <input type="number" defaultValue={r.quote_amount ?? ""} onBlur={(e) => e.target.value !== "" && e.target.value !== String(r.quote_amount ?? "") && patchLT(r.id, { quote_amount: Number(e.target.value), status: "preventivo_inviato" })} placeholder="preventivo €" className="h-7 w-24 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`lt-quote-${r.id}`} />
              <input defaultValue={r.quote_note || ""} onBlur={(e) => e.target.value !== (r.quote_note || "") && patchLT(r.id, { quote_note: e.target.value })} placeholder="note preventivo" className="h-7 w-40 rounded border border-gray-200 bg-white px-1 text-[10px]" data-testid={`lt-quote-note-${r.id}`} />
              {r.quote_amount != null && r.email && <button onClick={() => patchLT(r.id, { status: "preventivo_inviato", quote_amount: r.quote_amount, quote_note: r.quote_note })} className="h-7 rounded bg-fr-navy text-white text-[10px] font-bold px-2" data-testid={`lt-send-${r.id}`}>Invia preventivo</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PartnerTab = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [pct, setPct] = useState(10);
  const load = () => { api(token).get("/partners").then(({ data }) => { setPartners(data.partners); setPct(data.commission_pct); }).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const setStatus = async (id, status) => {
    try {
      await api(token).patch(`/partners/${id}`, { status });
      toast.success(status === "approved" ? "Partner approvato: può vendere le auto" : status === "suspended" ? "Partner sospeso" : "Partner rimesso in attesa");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const badge = (s) => (s === "approved" ? "bg-green-100 text-green-800" : s === "suspended" ? "bg-red-100 text-fr-red" : "bg-amber-100 text-amber-800");

  return (
    <div data-testid="admin-tab-partner">
      <p className="text-xs text-gray-500 mb-4">
        Hotel e B&B registrati al programma partner. Solo gli account <b>approvati</b> accedono all'area /partner. Commissione fissa per tutti: <b>{pct}%</b> del totale cliente su ogni prenotazione confermata o completata.
      </p>
      <button onClick={load} className="mb-3 h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200">
        <RefreshCw size={12} /> Aggiorna
      </button>
      {partners.length === 0 && <p className="text-sm text-gray-400">Nessun partner registrato.</p>}
      <div className="space-y-2" data-testid="admin-partners-list">
        {partners.map((p) => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`admin-partner-${p.id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-bold text-fr-navy text-sm">{p.business_name}</span>
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${badge(p.status)}`}>{p.status.toUpperCase()}</span>
              <span className="ml-auto text-[10px] text-gray-400">{format(new Date(p.created_at), "d MMM yyyy", { locale: it })}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">{p.contact_name} • {p.email} • {p.phone}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Prenotazioni: <b>{p.bookings_count}</b> • Guadagno maturato: <b className="text-green-700">€ {p.total_commission}</b> • in attesa conferma: € {p.pending_commission}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {p.status !== "approved" && (
                <button onClick={() => setStatus(p.id, "approved")} className="h-8 rounded-lg bg-green-600 text-white text-[11px] font-bold px-3 hover:bg-green-700" data-testid={`admin-partner-approve-${p.id}`}>
                  Approva
                </button>
              )}
              {p.status !== "suspended" && (
                <button onClick={() => setStatus(p.id, "suspended")} className="h-8 rounded-lg bg-red-50 text-fr-red text-[11px] font-bold px-3 hover:bg-red-100" data-testid={`admin-partner-suspend-${p.id}`}>
                  Sospendi
                </button>
              )}
              {p.status !== "pending" && (
                <button onClick={() => setStatus(p.id, "pending")} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 hover:bg-gray-200" data-testid={`admin-partner-pending-${p.id}`}>
                  Rimetti in attesa
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WhatsAppTab = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", whatsapp_from: "", sms_from: "" });
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState("");
  useEffect(() => {
    api(token).get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [token]);

  const save = async () => {
    try {
      await api(token).post("/settings", form);
      toast.success("Chiavi Twilio salvate: le conferme WhatsApp partono da ora");
      const { data } = await api(token).get("/settings");
      setSettings(data);
      setForm({ account_sid: "", auth_token: "", whatsapp_from: "", sms_from: "" });
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const testSend = async () => {
    setTestResult("…invio in corso");
    try {
      const { data } = await api(token).post("/settings/test-whatsapp", { to: testTo });
      setTestResult(`Inviato a ${data.to}`);
    } catch (e) {
      setTestResult(`Errore: ${formatDetail(e.response?.data?.detail)}`);
    }
  };

  return (
    <div data-testid="admin-tab-whatsapp">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-bold text-fr-navy mb-1">Conferme WhatsApp + SMS (Twilio)</p>
        <p className="text-[11px] text-gray-500 mb-3">
          Stato: <b data-testid="admin-wa-status">{settings?.ready ? "ATTIVO" : "NON ATTIVO"}</b> {settings?.source ? `(sorgente: ${settings.source})` : ""} — chiavi attuali: {settings?.account_sid || "—"} / WhatsApp: {settings?.whatsapp_from || "—"} / SMS: {settings?.sms_from || "—"}
        </p>
        <div className="grid gap-2">
          <input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="TWILIO_ACCOUNT_SID (AC…)" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-wa-sid" />
          <input value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="TWILIO_AUTH_TOKEN" type="password" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-wa-token" />
          <input value={form.whatsapp_from} onChange={(e) => setForm({ ...form, whatsapp_from: e.target.value })} placeholder="Numero mittente WhatsApp (es. +39 ... o sandbox)" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-wa-from" />
          <input value={form.sms_from} onChange={(e) => setForm({ ...form, sms_from: e.target.value })} placeholder="Mittente SMS (FABRIRENT o numero IT)" className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-wa-sms-from" />
        </div>
        <p className="text-[10px] text-gray-400 mt-2">
          Nota: Twilio non vende numeri +39. Per gli SMS in Italia usa il mittente alfanumerico "FABRIRENT" (già configurato) o un tuo numero verificato. Per WhatsApp verso tutti i clienti serve la registrazione WhatsApp Business (Meta).
        </p>
        <button onClick={save} className="mt-3 rounded-xl bg-fr-navy text-white font-bold text-sm px-5 py-2.5 hover:bg-fr-navydark flex items-center gap-2" data-testid="admin-wa-save">
          <Check size={14} /> Salva chiavi
        </button>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-bold text-gray-500 mb-2">Test invio WhatsApp</p>
          <div className="flex gap-2">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+39 350 000 0000" className="h-9 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-wa-test-to" />
            <button onClick={testSend} className="h-9 rounded-lg bg-green-600 text-white text-xs font-bold px-4 hover:bg-green-700" data-testid="admin-wa-test-send">Invia test</button>
          </div>
          {testResult && <p className="mt-2 text-[11px] text-gray-600" data-testid="admin-wa-test-result">{testResult}</p>}
        </div>
      </div>
    </div>
  );
};

const PasswordTab = ({ token, onLogout }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const submit = async () => {
    if (nw !== conf) { toast.error("Le due password non coincidono"); return; }
    try {
      await api(token).post("/password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata: ora entra con la nuova");
      onLogout();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };
  const [conf, setConf] = useState("");
  return (
    <div className="max-w-sm" data-testid="admin-panel-password">
      <label className="block mb-3">
        <span className="text-[10px] font-bold text-gray-400 uppercase">Password attuale</span>
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-password-current" />
      </label>
      <label className="block mb-4">
        <span className="text-[10px] font-bold text-gray-400 uppercase">Nuova password (min 12)</span>
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-password-new" />
      </label>
      <label className="block mb-4">
        <span className="text-[10px] font-bold text-gray-400 uppercase">Conferma nuova password</span>
        <input type="password" value={conf} onChange={(e) => setConf(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy" data-testid="admin-password-confirm" />
      </label>
      <button onClick={submit} disabled={nw.length < 12 || nw !== conf} className="rounded-xl bg-fr-navy text-white font-bold text-sm px-6 py-3 hover:bg-fr-navydark disabled:opacity-50" data-testid="admin-password-submit">Aggiorna password</button>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("fr_admin_token"));
  const [tab, setTab] = useState("Prezzi");
  const [me, setMe] = useState(null);
  const [viewDocAdminSrc, setViewDocAdminSrc] = useState(null);
  const [mustChange, setMustChange] = useState(false);
  const [reauth, setReauth] = useState(null); // {password} dialog per operazioni sensibili (A3)

  // A3 (ITERAZIONE 4): esegue una chiamata sensibile; su 428 chiede la password (con MFA se attivo),
  // ottiene un token di riautenticazione (5 min) e riprova automaticamente.
  const secure = (token_) => async (fn) => {
    const cached = _reauthCache.tok && _reauthCache.exp > Date.now() ? { "X-Reauth-Token": _reauthCache.tok } : null;
    try {
      return await fn(cached || {});
    } catch (e) {
      if (e.response?.status !== 428) throw e;
      const pw = await new Promise((resolve) => setReauth({ resolve, email: me?.email }));
      if (!pw) throw e;
      const { data } = await api(token_).post("/reauth", { password: pw.password, totp_code: pw.totp_code || undefined });
      _reauthCache.tok = data.reauth_token;
      _reauthCache.exp = Date.now() + 280000;
      setReauth(null);
      return await fn({ "X-Reauth-Token": data.reauth_token });
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const emailDec = JSON.parse(atob((localStorage.getItem("fr_admin_token") || "").split(".")[1] || "e30="))?.sub;
        setMe({ email: emailDec, role: d.users?.find((u) => u.email === emailDec)?.role });
      })
      .catch(() => {});
  }, [token]);

  const viewDocAdmin = async (path) => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/account/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setViewDocAdminSrc(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    }
  };

  const onLogin = (access, refresh, mustChange) => {
    localStorage.setItem("fr_admin_token", access);
    localStorage.setItem("fr_admin_refresh", refresh);
    setToken(access);
    if (mustChange) {
      setTab("Password");
      setMustChange(true);
      toast.warning("Sicurezza: devi cambiare la password prima di proseguire");
    } else {
      toast.success("Bentornato nel gestionale");
    }
  };
  const onLogout = () => {
    localStorage.removeItem("fr_admin_token");
    localStorage.removeItem("fr_admin_refresh");
    setToken(null);
  };

  if (!token) return <Login onLogin={onLogin} />;

  const _secure = secure(token);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {mustChange && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs font-semibold px-4 py-2" data-testid="admin-must-change-banner">
          Sicurezza: cambiare la password è obbligatorio prima di usare il gestionale (tab Password)
        </div>
      )}
      <header className="bg-fr-navy text-white h-14 flex items-center px-4 sticky top-0 z-20">
        <FgLogo size={32} />
        <span className="ml-3 font-extrabold tracking-tight">Gestionale FabriRent</span>
        <button onClick={onLogout} className="ml-auto flex items-center gap-1.5 text-xs font-semibold bg-white/10 rounded-lg px-3 py-2 hover:bg-white/20" data-testid="admin-logout">
          <LogOut size={13} /> Esci
        </button>
      </header>
      <nav className="bg-white border-b border-gray-100 px-4 flex gap-1 overflow-x-auto no-scrollbar sticky top-14 z-10">
        {TABS.map((t) => (
          (t === "Gestionale" && me?.role !== "titolare") ? null : (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`shrink-0 px-4 py-3 text-xs font-bold border-b-2 transition-colors ${tab === t ? "border-fr-red text-fr-navy" : "border-transparent text-gray-400 hover:text-fr-navy"}`}
            data-testid={`admin-tab-${t.toLowerCase().replace(/\s/g, "")}`}
          >
            {t}
          </button>)
        ))}
      </nav>
      <main className="max-w-4xl mx-auto p-4">
        {tab === "Dashboard" && <DashboardTab token={token} api={api} />}
        {tab === "Gestionale" && me?.role === "titolare" && <GestionaleTab token={token} me={me} />}
        {tab === "Prezzi" && <PrezziTab token={token} />}
        {tab === "Flotta" && <FlottaTab token={token} />}
        {tab === "Prenotazioni" && <PrenotazioniTab token={token} />}
        {tab === "Sinistri" && <SinistriTab token={token} api={api} viewDoc={viewDocAdmin} />}
        {tab === "Consegne" && <ConsegneTab token={token} api={api} secure={_secure} />}
        {tab === "Multe" && <MulteTab token={token} api={api} />}
        {tab === "Pagamenti" && <PagamentiTab token={token} api={api} secure={_secure} />}
        {tab === "Utenti" && <UtentiTab token={token} api={api} me={me} secure={_secure} />}
        {tab === "Impostazioni" && <ImpostazioniTab token={token} api={api} secure={_secure} />}
        {tab === "Clienti" && <ClientiTab token={token} />}
        {tab === "Richieste LT" && <RichiesteTab token={token} />}
        {tab === "Partner" && <PartnerTab token={token} />}
        {tab === "WhatsApp" && <WhatsAppTab token={token} />}
        {tab === "Password" && <PasswordTab token={token} onLogout={onLogout} me={me} />}
      </main>
      <Dialog open={!!viewDocAdminSrc} onOpenChange={(o) => !o && setViewDocAdminSrc(null)}>
        <DialogContent className="max-w-md" data-testid="admin-doc-dialog-global">
          <DialogTitle>Documento cliente</DialogTitle>
          {viewDocAdminSrc && <img src={viewDocAdminSrc} alt="Documento" className="w-full rounded-lg border border-gray-100" />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reauth} onOpenChange={(o) => { if (!o) { reauth?.resolve(null); setReauth(null); } }}>
        <DialogContent className="max-w-sm" data-testid="reauth-dialog">
          <DialogTitle>Conferma la tua identità</DialogTitle>
          <p className="text-xs text-gray-500 mb-2">Operazione sensibile: inserisci la password (e il codice MFA se attivo). Validità 5 minuti.</p>
          <form onSubmit={(e) => { e.preventDefault(); const v = document.getElementById("reauth-pw")?.value; const t = document.getElementById("reauth-totp")?.value; reauth?.resolve({ password: v, totp_code: t }); setReauth(null); }}>
            <input id="reauth-pw" type="password" required placeholder="Password" className="w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="reauth-password" />
            <input id="reauth-totp" type="text" inputMode="numeric" placeholder="Codice MFA (se attivo)" className="mt-2 w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="reauth-totp" />
            <button type="submit" className="mt-3 w-full rounded-xl bg-fr-navy text-white font-bold text-sm px-4 py-2.5" data-testid="reauth-confirm">Conferma</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
